import React from 'react';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Divider } from '@mui/material';

import { getBearingEnvelopeSelectableType, SelectedItemIdentifier } from '../../../../library/selection/types';
import { TransformationMenuItems } from '../../../../library/transformations/controls/TransformationMenuItems';
import { getBearingEnvelopeUserVisibleTerm } from '../../../../library/utilities/bearingEnvelopeUserTerm';
import { useAppMonitor } from '../../../../monitor';
import { editBearingEnvelopes } from '../../../../store/actions/layout/bearingEnvelopeActions';
import { startTrimOrExtendEnvelopes } from '../../../../store/actions/layout/trussEnvelopeActions';
import {
  handleStandardSceneItemEditPropertiesAction,
  handleStandardSceneItemsEditPropertiesAction,
} from '../../../../store/actions/sceneInteractionActions';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { flipBearingEnvelope } from '../../../interface/subpanels/properties/roof-planes/bearing-envelopes/flipBearingEnvelope';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { DeleteMenuItem } from '../shared-menu-items/DeleteMenuItem';

export function BearingEnvelopeMenu(props: { guid: string }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const bearingEnvelopes = useAppSelector((state) => state.layout.bearingEnvelopes);
  const bearingEnvelope = bearingEnvelopes[props.guid];

  if (bearingEnvelope == null) throw new Error('Unable to find bearing envelope with active menu.');

  const selectableType = getBearingEnvelopeSelectableType(bearingEnvelope);
  const userVisibleTerm = getBearingEnvelopeUserVisibleTerm(bearingEnvelope.bearingType);

  return (
    <SceneMenuContent>
      <SceneMenuItem
        text="Edit Properties"
        icon={<EditIcon />}
        onClick={() => {
          dispatch(handleStandardSceneItemEditPropertiesAction(selectableType, bearingEnvelope));
          monitor.recordInteraction('Bearing Envelope => Edit Properties menu item clicked');
        }}
      />

      <TransformationMenuItems />

      <SceneMenuItem
        text={`Flip ${userVisibleTerm}`}
        icon={<SwapHorizIcon />}
        onClick={async () => {
          await dispatch(
            editBearingEnvelopes({
              guid: bearingEnvelope.guid,
              editedProperties: flipBearingEnvelope(bearingEnvelope),
            }),
          );

          monitor.recordInteraction('Bearing Envelope => Flip Bearing menu item clicked');
        }}
      />

      <SceneMenuItem
        text="Trim/Extend"
        icon={<CompareArrowsIcon />}
        onClick={async () => {
          dispatch(startTrimOrExtendEnvelopes());
          monitor.recordInteraction('Bearing Envelope => Trim/Extend menu item clicked');
        }}
      />

      <Divider />

      <DeleteMenuItem
        type={selectableType}
        item={bearingEnvelope}
        onBeforeDelete={(proceed) => {
          if (confirm(`Are you sure you want to delete this ${userVisibleTerm.toLowerCase()}?`)) {
            proceed();
          } else {
            monitor.recordInteraction('Bearing Envelope => Delete menu item clicked, but cancelled');
          }
        }}
      />
    </SceneMenuContent>
  );
}

export function BulkBearingEnvelopeMenu(props: { type: 'beam' | 'wall'; items: SelectedItemIdentifier[] }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const bearingEnvelopeStore = useAppSelector((state) => state.layout.bearingEnvelopes);
  const bearingEnvelopes = props.items.map((item) => bearingEnvelopeStore[item.guid]);
  const userVisiblePluralTerm = getBearingEnvelopeUserVisibleTerm(props.type, { plural: true });

  return (
    <SceneMenuContent>
      <SceneMenuItem
        text="Edit Properties"
        icon={<EditIcon />}
        onClick={() => {
          dispatch(handleStandardSceneItemsEditPropertiesAction(props.type, bearingEnvelopes));
          monitor.recordInteraction('Bearing Envelopes => Edit Properties menu item clicked');
        }}
      />

      <TransformationMenuItems />

      <SceneMenuItem
        text="Trim/Extend"
        icon={<CompareArrowsIcon />}
        onClick={async () => {
          dispatch(startTrimOrExtendEnvelopes());
          monitor.recordInteraction('Bearing Envelopes => Trim/Extend menu item clicked');
        }}
      />

      <Divider />

      <DeleteMenuItem
        type={props.type}
        userVisibleTerm={userVisiblePluralTerm}
        item={props.items}
        onBeforeDelete={(proceed) => {
          if (confirm(`Are you sure you want to delete these ${userVisiblePluralTerm.toLowerCase()}?`)) {
            proceed();
          } else {
            monitor.recordInteraction('Bearing Envelopes => Delete Bearing Envelopes menu item clicked, but cancelled');
          }
        }}
      />
    </SceneMenuContent>
  );
}
